import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { DropdownContext } from 'components/DropdownContext';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { RequestStatus } from 'features/request/requestSlice';
import type { CatalogWorkTypeData } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useDropdownContextMenuTooltipController } from 'hooks/useDropdownContextMenuTooltipController';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { Tooltip } from 'uikit';

import { useCallback, useMemo } from 'react';

import type { IDataEditModal } from './CatalogTypesWork';
import { useRemoveTypeWork } from './hooks/useRemoveTypeWork';

import Styles from './catalogTypesWork.module.css';

interface IRowMenu {
    data: CatalogWorkTypeData;
    createOrEditModalHandler: (state: boolean) => void;
    setDataEditModal: React.Dispatch<React.SetStateAction<IDataEditModal | null>>;
    onRemoveSuccess: () => void;
    catalogTypesOfWorkRules: IUseAccess['catalogTypesOfWork'];
}

export const RowMenu: React.FC<IRowMenu> = ({
    data,
    createOrEditModalHandler,
    setDataEditModal,
    onRemoveSuccess,
    catalogTypesOfWorkRules,
}) => {
    const {
        catalogRemoveWorkTypeRequest,
        catalogRemoveWorkTypeStatus,
        catalogRemoveWorkTypeData,
        catalogRemoveWorkTypeError,
        isSubmitted,
        handleCloseModalError,
        handleCreateReport,
    } = useRemoveTypeWork([data.id], onRemoveSuccess);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: () => catalogRemoveWorkTypeRequest(),
        });

    const isDisabledDeleteButton = data.hasPrice || catalogRemoveWorkTypeStatus === RequestStatus.FETCHING;

    const dataEditModal = useMemo(
        () => ({
            code: data?.code,
            name: data?.name,
            unit: data?.unit,
            id: data?.id,
        }),
        [data?.code, data?.id, data?.name, data?.unit],
    );

    const onOpenEditModalHandler = useCallback(() => {
        createOrEditModalHandler(true);
        setDataEditModal(dataEditModal);
    }, [createOrEditModalHandler, dataEditModal, setDataEditModal]);

    const items = [
        {
            label: 'Редактировать',
            action: onOpenEditModalHandler,
            disabled: !catalogTypesOfWorkRules.canEdit,
        },
        {
            label: 'Удалить',
            haveTooltip: isDisabledDeleteButton,
            disabled: isDisabledDeleteButton || !catalogTypesOfWorkRules.canDelete,
            tooltipName: 'cantDeleteWorkTypes',
            tooltipMsg: 'Имеется связь с расценкой',
            action: handleOpenConfirmationDialog,
        },
    ];

    const { getMenuItemAttributes, tooltipPosition, activeTooltipMsg, onMenuMouseMove, onMenuMouseLeave } =
        useDropdownContextMenuTooltipController();

    return (
        <>
            <DropdownContext
                getItemAttributesHandler={getMenuItemAttributes}
                onMouseMoveHandler={onMenuMouseMove}
                onMouseLeaveHandler={onMenuMouseLeave}
                items={items}
                direction="downStartRight"
                wrapperClassName={Styles.wrapperDropdown}
            />
            {activeTooltipMsg && (
                <Tooltip
                    size="m"
                    direction="rightCenter"
                    position={tooltipPosition}
                    isInteractive={false}
                    style={{ zIndex: 2 }}
                >
                    {activeTooltipMsg}
                </Tooltip>
            )}
            <ConfirmationDialog
                textHeader="удаление записи"
                textConfirmation="Вы уверены, что хотите удалить запись?"
                confirmButtonText="Удалить"
                cancelButtonText="Не удалять"
                onSubmit={handleActionQuery}
                isOpened={isOpenConfirmationDialog}
                onClose={handleCloseConfirmationDialog}
            />
            <ErrorHandlingModal
                titleText="Удаление вида работ"
                customButtonText="Понятно"
                viewButton="primary"
                isSubmitted={isSubmitted}
                statusRequest={catalogRemoveWorkTypeStatus}
                dataRequest={catalogRemoveWorkTypeData?.catalogRemoveWorkTypes}
                errorsRequest={catalogRemoveWorkTypeError}
                handleCloseModalError={handleCloseModalError}
                handleCreateReport={handleCreateReport}
            />
        </>
    );
};

RowMenu.displayName = 'RowMenu';
